import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { invoiceApi } from "../services/invoiceApi";
import FeedbackModal from "../components/ui/FeedbackModal";
import ConfirmDialog from "../components/ui/ConfirmDialog";
import InvoiceFormModal from "../components/invoices/InvoiceFormModal";
import api from "../services/api";

export default function Invoices() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState({
    totalInvoiced: 0,
    paidAmount: 0,
    pendingAmount: 0,
    overdueCount: 0,
  });

  const [vendors, setVendors] = useState([]);
  const [costPrices, setCostPrices] = useState([]);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [selectedIds, setSelectedIds] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);

  const [deleteOneId, setDeleteOneId] = useState(null);
  const [message, setMessage] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
  });

  const openMessage = (type, messageText, title = type === "success" ? "Success" : "Error") => {
    setMessage({
      open: true,
      type,
      title,
      message: messageText,
    });
  };

  const closeMessage = () => {
    setMessage((prev) => ({ ...prev, open: false }));
  };

  const loadInvoices = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (search.trim()) params.set("search", search.trim());

      const res = await invoiceApi.list(params.toString() ? `?${params.toString()}` : "");
      setInvoices(res.data?.invoices || []);
      setSummary(
        res.data?.summary || {
          totalInvoiced: 0,
          paidAmount: 0,
          pendingAmount: 0,
          overdueCount: 0,
        }
      );
      setSelectedIds([]);
    } catch (err) {
      openMessage("error", err.response?.data?.message || "Failed to load invoices");
    } finally {
      setLoading(false);
    }
  };

  const loadLookups = async () => {
    try {
      const [vendorsRes, costPricesRes] = await Promise.allSettled([
        api.get("/vendors"),
        api.get("/cost-prices"),
      ]);

      if (vendorsRes.status === "fulfilled") {
        const data = vendorsRes.value.data;
        setVendors(Array.isArray(data?.vendors) ? data.vendors : data || []);
      }

      if (costPricesRes.status === "fulfilled") {
        const data = costPricesRes.value.data;
        setCostPrices(Array.isArray(data?.costPrices) ? data.costPrices : data || []);
      }
    } catch (err) {
      // ignore lookup failures so the page still works
    }
  };

  useEffect(() => {
    loadLookups();
  }, []);

  useEffect(() => {
    loadInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadInvoices();
    }, 350);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const allSelected = useMemo(
    () => invoices.length > 0 && selectedIds.length === invoices.length,
    [invoices.length, selectedIds.length]
  );

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(invoices.map((inv) => inv.id));
    }
  };

  const toggleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const openCreate = () => {
    navigate("/invoices/create");
  };

  const openEdit = async (id) => {
    try {
      const res = await invoiceApi.getById(id);
      setEditingInvoice(res.data?.invoice || null);
      setFormOpen(true);
    } catch (err) {
      openMessage("error", err.response?.data?.message || "Failed to load invoice");
    }
  };

  const handleDeleteOne = async () => {
    try {
      setSaving(true);
      await invoiceApi.remove(deleteOneId);
      setDeleteOneId(null);
      openMessage("success", "Invoice deleted successfully");
      loadInvoices();
    } catch (err) {
      openMessage("error", err.response?.data?.message || "Failed to delete invoice");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (!selectedIds.length) return;

    setSaving(true);
    try {
      await Promise.all(selectedIds.map((id) => invoiceApi.remove(id)));
      setSelectedIds([]);
      openMessage("success", "Selected invoices deleted successfully");
      loadInvoices();
    } catch (err) {
      openMessage("error", err.response?.data?.message || "Failed to delete selected invoices");
    } finally {
      setSaving(false);
    }
  };

  const openPrint = (id) => {
    navigate(`/invoices/${id}/print`);
  };

  const statusBadge = (status) => {
    const base =
      "inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold capitalize";
    switch (status) {
      case "paid":
        return `${base} bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300`;
      case "pending":
        return `${base} bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300`;
      case "partial":
        return `${base} bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300`;
      case "overdue":
        return `${base} bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300`;
      default:
        return `${base} bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300`;
    }
  };

  const currency = invoices[0]?.currency || "GBP";

  const shell =
    "rounded-2xl border border-slate-200 bg-white shadow-sm transition-colors duration-300 dark:border-slate-800 dark:bg-slate-900";
  const input =
    "w-72 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-blue-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-500";

  return (
    <div className="p-6 text-slate-900 dark:text-slate-100">
      <FeedbackModal
        open={message.open}
        type={message.type}
        title={message.title}
        message={message.message}
        onClose={closeMessage}
      />

      <ConfirmDialog
        open={deleteOneId !== null}
        title="Delete invoice"
        message="This invoice will be removed permanently."
        confirmText="Delete"
        cancelText="Cancel"
        onCancel={() => setDeleteOneId(null)}
        onConfirm={handleDeleteOne}
      />

      <InvoiceFormModal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={(msg) => {
          openMessage("success", msg || "Invoice saved successfully");
          loadInvoices();
        }}
        invoice={editingInvoice}
        vendors={vendors}
        costPrices={costPrices}
      />

      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 dark:text-slate-100">
            Invoices
          </h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Manage billing, invoice items, printing, and payment status.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search invoices..."
            className={input}
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-blue-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
          >
            <option value="all">All</option>
            <option value="paid">Paid</option>
            <option value="pending">Pending</option>
            <option value="partial">Partial</option>
            <option value="overdue">Overdue</option>
            <option value="draft">Draft</option>
          </select>

          <button
            onClick={openCreate}
            className="rounded-xl bg-blue-600 px-5 py-3 text-sm font-medium text-white transition hover:bg-blue-700"
          >
            + Create Invoice
          </button>

          <button
            onClick={handleDeleteSelected}
            disabled={!selectedIds.length || saving}
            className="rounded-xl border border-red-200 px-5 py-3 text-sm font-medium text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-900/50 dark:text-red-300 dark:hover:bg-red-950/40"
          >
            Delete Selected
          </button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className={`${shell} p-5`}>
          <p className="text-sm text-slate-500 dark:text-slate-400">Total Invoiced</p>
          <p className="mt-2 text-2xl font-semibold text-slate-900 dark:text-slate-100">
            {currency} {Number(summary.totalInvoiced || 0).toFixed(2)}
          </p>
        </div>
        <div className={`${shell} p-5`}>
          <p className="text-sm text-slate-500 dark:text-slate-400">Paid Amount</p>
          <p className="mt-2 text-2xl font-semibold text-slate-900 dark:text-slate-100">
            {currency} {Number(summary.paidAmount || 0).toFixed(2)}
          </p>
        </div>
        <div className={`${shell} p-5`}>
          <p className="text-sm text-slate-500 dark:text-slate-400">Pending Amount</p>
          <p className="mt-2 text-2xl font-semibold text-slate-900 dark:text-slate-100">
            {currency} {Number(summary.pendingAmount || 0).toFixed(2)}
          </p>
        </div>
        <div className={`${shell} p-5`}>
          <p className="text-sm text-slate-500 dark:text-slate-400">Overdue</p>
          <p className="mt-2 text-2xl font-semibold text-slate-900 dark:text-slate-100">
            {summary.overdueCount || 0} Invoices
          </p>
        </div>
      </div>

      <div className={`${shell} mt-6 p-5`}>
        <div className="flex items-center justify-between gap-4 border-b border-slate-100 pb-4 dark:border-slate-800">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              Invoice Overview
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Track all invoices and related cost price items.
            </p>
          </div>

          <div className="text-sm text-slate-500 dark:text-slate-400">
            Showing {invoices.length} invoices
          </div>
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:bg-slate-800 dark:text-slate-300">
              <tr>
                <th className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                    className="h-4 w-4 rounded border-slate-300 text-blue-600 dark:border-slate-600"
                  />
                </th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">REF</th>
                <th className="px-4 py-3">Supplier</th>
                <th className="px-4 py-3">Total ({currency})</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {loading ? (
                <tr>
                  <td
                    colSpan="7"
                    className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400"
                  >
                    Loading invoices...
                  </td>
                </tr>
              ) : invoices.length === 0 ? (
                <tr>
                  <td
                    colSpan="7"
                    className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400"
                  >
                    No invoices found.
                  </td>
                </tr>
              ) : (
                invoices.map((inv) => (
                  <tr
                    key={inv.id}
                    className="border-t border-slate-100 bg-white transition hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:hover:bg-slate-800/60"
                  >
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(inv.id)}
                        onChange={() => toggleSelectOne(inv.id)}
                        className="h-4 w-4 rounded border-slate-300 text-blue-600 dark:border-slate-600"
                      />
                    </td>
                    <td className="px-4 py-4 text-sm text-slate-700 dark:text-slate-300">
                      {inv.invoiceDate || "-"}
                    </td>
                    <td className="px-4 py-4 font-semibold text-slate-800 dark:text-slate-100">
                      {inv.invoiceNumber || inv.ref || `INV ${inv.id}`}
                    </td>
                    <td className="px-4 py-4 text-sm text-slate-700 dark:text-slate-300">
                      {inv.supplierName || inv.vendor?.name || inv.vendor?.companyName || "-"}
                    </td>
                    <td className="px-4 py-4 font-semibold text-slate-800 dark:text-slate-100">
                      {currency} {Number(inv.totalAmount || 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-4">
                      <span className={statusBadge(inv.status)}>{inv.status}</span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => openEdit(inv.id)}
                          className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => openPrint(inv.id)}
                          className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                        >
                          Print
                        </button>
                        <button
                          onClick={() => setDeleteOneId(inv.id)}
                          className="rounded-xl border border-red-200 px-3 py-2 text-sm text-red-600 transition hover:bg-red-50 dark:border-red-900/50 dark:text-red-300 dark:hover:bg-red-950/40"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}